import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useAuth } from "@clerk/react";
import { useAuthedApi } from "./api";
import { useSite } from "./site";

// Customer's choice ("dark" | "light" | "auto") wins; otherwise the admin-set default applies.
const KEY = "csm_theme";
const DEF_KEY = "csm_theme_default";
const ThemeCtx = createContext({ pref: "dark", resolved: "dark", setPref: () => {} });
const mq = () => window.matchMedia("(prefers-color-scheme: light)");

export const resolveTheme = (p) => (p === "auto" ? (mq().matches ? "light" : "dark") : p === "light" ? "light" : "dark");

const apply = (r) => {
  const el = document.documentElement;
  el.classList.toggle("light", r === "light");
  el.classList.toggle("dark", r !== "light");
  document.querySelector('meta[name="theme-color"]')?.setAttribute("content", r === "light" ? "#FAF8F3" : "#08090C");
};

export function ThemeProvider({ children }) {
  const { isSignedIn } = useAuth();
  const api = useAuthedApi();
  const { branding, me } = useSite();
  const [own, setOwn] = useState(() => localStorage.getItem(KEY));
  const [, tick] = useState(0);
  const def = branding.default_theme || "dark";
  const pref = own || def;
  const resolved = resolveTheme(pref);

  useEffect(() => { localStorage.setItem(DEF_KEY, def); }, [def]);
  useEffect(() => { apply(resolved); }, [resolved]);
  useEffect(() => {
    if (pref !== "auto") return;
    const m = mq();
    const on = () => tick((x) => x + 1);
    m.addEventListener("change", on);
    return () => m.removeEventListener("change", on);
  }, [pref]);
  useEffect(() => {
    // A signed-in customer's saved choice follows them to new devices.
    const saved = me?.prefs?.theme;
    if (saved && !localStorage.getItem(KEY)) { localStorage.setItem(KEY, saved); setOwn(saved); }
  }, [me?.prefs?.theme]);

  const setPref = useCallback((p) => {
    localStorage.setItem(KEY, p);
    setOwn(p);
    if (isSignedIn) api("put", "/me/prefs", { theme: p }).catch(() => {});
  }, [isSignedIn, api]);
  return <ThemeCtx.Provider value={{ pref, resolved, setPref }}>{children}</ThemeCtx.Provider>;
}

export const useTheme = () => useContext(ThemeCtx);

// For components outside ThemeProvider (e.g. Clerk): watch the <html> class.
export function useHtmlTheme() {
  const get = () => (document.documentElement.classList.contains("light") ? "light" : "dark");
  const [t, setT] = useState(get);
  useEffect(() => {
    const o = new MutationObserver(() => setT(get()));
    o.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => o.disconnect();
  }, []);
  return t;
}
